import type { ReactNode } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_RowData,
  type MRT_TableOptions,
} from 'material-react-table';
import {
  backgroundColors,
  brand,
  separatorByRung,
  tableHeaderByRung,
  textColors,
  type SurfaceRung,
} from '@/tokens/colors';
import { radius } from '@/tokens/radius';
import { dsVar } from '@/tokens/surfaceScopes';
import { spacing } from '@/tokens/spacing';
import { fontSize, fontWeight } from '@/tokens/typography';

/**
 * Table (DataTable)
 * Classification: mui-wrapper (wraps material-react-table)
 * Base: https://www.material-react-table.com/
 *
 * The product's standard list view. With nothing but `columns` and `data` it
 * renders the shape every AiStrike surface actually wants: a bordered table, a
 * recessed uppercase header, comfortable rows with a hairline rule between
 * them, and hover feedback. Spreadsheet behaviour — toolbar, pagination,
 * sorting, column menus — is off until you ask for it.
 *
 * Compose rows out of the exported cell primitives (`TableTitleCell`,
 * `TableStatusCell`, `TableMetricCell`, `TableMetaCell`, `TableChipsCell`,
 * `TableActionsCell`) rather than hand-building cells from Box/Typography.
 *
 * Do not restyle this component through `muiTablePaperProps` descendant
 * selectors or hardcoded hex — every value it uses is a token, and the props
 * below cover the cases that used to require an override.
 */

/** Background rung the table paints onto, or `transparent` to inherit. */
export type TableSurface = SurfaceRung | 'transparent';

/** Row rhythm. `compact` is for sub-tables, drawers and dialogs. */
export type TableDensity = 'comfortable' | 'compact';

const ROW_PAD: Record<TableDensity, { y: string; x: string }> = {
  comfortable: { y: '14px', x: `${spacing.md}px` },
  compact: { y: `${spacing.sm}px`, x: `${spacing.sm + spacing.xs}px` },
};

export interface DataTableProps<T extends MRT_RowData>
  extends Omit<MRT_TableOptions<T>, 'columns' | 'data'> {
  columns: MRT_ColumnDef<T>[];
  data: T[];

  /**
   * Makes the whole row activate. Wires click, Enter/Space, `tabIndex`,
   * `aria-selected` and the pointer cursor — you do not need to add any of it.
   */
  onRowClick?: (row: T) => void;
  /**
   * Marks a row as the current one. Matched against the row's id, which is
   * `row.id` on your data unless you pass your own `getRowId`.
   */
  selectedRowId?: string | number;
  /** Rows this returns true for render at 72% opacity (drafts, disabled records). */
  isRowDimmed?: (row: T) => boolean;

  /** Show skeleton rows in place of content. */
  loading?: boolean;
  /**
   * What to show when there are no rows. Distinguish "nothing exists yet" from
   * "nothing matched the filter" — they need different words.
   */
  empty?: ReactNode;

  /** Pin the header while the body scrolls. Pair with `maxHeight`. */
  sticky?: boolean;
  /** Cap the body height and scroll inside the card. */
  maxHeight?: number | string;
  /** Row rhythm. Defaults to `comfortable`. */
  density?: TableDensity;

  /** Show the top toolbar (global search, filter and density controls). */
  toolbar?: boolean;
  /** Show the pagination footer. */
  pagination?: boolean;
  /** Allow sorting from the column headers. */
  sortable?: boolean;
  /** Show the per-column action menu (hide, group, filter). */
  columnActions?: boolean;

  /**
   * Which background the table paints. Defaults to the host rung via
   * `--ds-bg`, so a table in a dialog does not paint app-shell colours. Pass a
   * ladder rung to pin an absolute fill, or `'transparent'` to dissolve into a
   * container that already provides its own surface and border.
   */
  surface?: TableSurface;
  /** Draw the rounded outline. On by default; turn off inside a Card. */
  bordered?: boolean;
}

export function DataTable<T extends MRT_RowData>({
  columns,
  data,
  onRowClick,
  selectedRowId,
  isRowDimmed,
  loading = false,
  empty,
  sticky = false,
  maxHeight,
  density = 'comfortable',
  toolbar = false,
  pagination = false,
  sortable = false,
  columnActions = false,
  surface,
  bordered = true,
  ...rest
}: DataTableProps<T>) {
  // No `surface` resolves through the scope so the body matches whatever hosts
  // the table. An explicit rung stays absolute: asking for `widget` is asking
  // for that colour.
  const base =
    surface === 'transparent'
      ? 'transparent'
      : surface
        ? backgroundColors[surface]
        : dsVar('bg', backgroundColors.primary);
  // Borderless tables sit edge-to-edge in a Card; match the card's 24px title
  // gutter so the first column lines up with the heading.
  const pad = {
    y: ROW_PAD[density].y,
    x: bordered ? ROW_PAD[density].x : `${spacing.lg}px`,
  };
  const interactive = Boolean(onRowClick);

  const table = useMaterialReactTable({
    columns,
    data,

    // Default row identity to the record's own `id` so `selectedRowId` works
    // without every call site declaring a `getRowId`.
    getRowId: (row: T, index: number) =>
      (row as { id?: string | number }).id != null
        ? String((row as { id?: string | number }).id)
        : String(index),

    enableTopToolbar: toolbar,
    enableBottomToolbar: pagination,
    enablePagination: pagination,
    enableSorting: sortable,
    enableColumnActions: columnActions,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableStickyHeader: sticky,
    // Honours the widths declared on columns instead of letting content decide.
    layoutMode: 'grid',

    ...(empty ? { renderEmptyRowsFallback: () => <>{empty}</> } : {}),

    /**
     * All cell styling lives here, as descendant selectors on the paper.
     *
     * It must NOT go in `muiTableHeadCellProps` / `muiTableBodyCellProps`:
     * material-react-table replaces table-level cell props wholesale for any
     * column that declares its own, which silently drops the styling under
     * some columns and not others — most visibly as row rules that stop
     * partway across the table.
     */
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        backgroundColor: base,
        backgroundImage: 'none',
        boxShadow: 'none',
        border: bordered ? `1px solid ${dsVar('border', backgroundColors.border)}` : 'none',
        borderRadius: bordered ? `${radius.md}px` : 0,
        overflow: 'hidden',

        '& thead th': {
          // ~1.08:1 recessed fill keyed to the host. Lighter in dark / darker in
          // light — away from the elevation direction.
          backgroundColor: dsVar('tableHeader', tableHeaderByRung.primary),
          backgroundImage: 'none',
          // A filled header eats the margin `subtle` was tuned for; labels step
          // up to tertiary (secondary at overlay). Unfilled headers would keep
          // `--ds-text-quiet` instead.
          color: dsVar('tableHeaderLabel', textColors.tertiary),
          fontSize: `${fontSize.xs}px`,
          fontWeight: fontWeight.semibold,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          lineHeight: 1.4,
          paddingTop: `${spacing.sm + spacing.xxs}px`,
          paddingBottom: `${spacing.sm + spacing.xxs}px`,
          paddingLeft: pad.x,
          paddingRight: pad.x,
          // MRT defaults head cells to `vertical-align: top`, which floats short
          // labels above taller select checkboxes.
          verticalAlign: 'middle',
        },
        // `.MuiTableCell-head` is required so this beats the theme's fill-only
        // `borderBottom: none` (two classes). Fill or underline, not both —
        // sticky is the exception: at 1.08 the fill can lose its edge while
        // rows scroll under it, so it also takes the separator as a bottom rule.
        '& thead .MuiTableCell-head, & thead .MuiTableCell-head.MuiTableCell-stickyHeader': {
          borderBottom: sticky
            ? `1px solid ${dsVar('separator', separatorByRung.primary)}`
            : 'none',
        },
        '& thead th .Mui-TableHeadCell-Content': { alignItems: 'center' },
        '& thead th .Mui-TableHeadCell-Content-Labels': { alignItems: 'center' },
        '& thead th .Mui-TableHeadCell-Content-Actions': {
          display: 'flex',
          alignItems: 'center',
        },

        '& tbody td': {
          backgroundColor: 'transparent',
          // Host-aware: dark tokens are near-white and wash out on light paper.
          color: dsVar('text', textColors.primary),
          fontSize: `${fontSize.md}px`,
          lineHeight: 1.45,
          paddingTop: pad.y,
          paddingBottom: pad.y,
          paddingLeft: pad.x,
          paddingRight: pad.x,
          // Row rhythm at ~1.12:1. The container outline stays on the heavier
          // `--ds-border` so the edge still reads as structure.
          borderBottom: `1px solid ${dsVar('separator', separatorByRung.primary)}`,
        },
        // The container already draws the outline; a rule under the final row
        // doubles up against it.
        '& tbody tr:last-of-type td': { borderBottom: 'none' },
      },
    },

    muiTableContainerProps: {
      sx: {
        backgroundColor: 'transparent',
        ...(maxHeight ? { maxHeight } : {}),
      },
    },
    muiTopToolbarProps: { sx: { backgroundColor: 'transparent' } },
    muiBottomToolbarProps: {
      sx: { backgroundColor: 'transparent', boxShadow: 'none' },
    },

    muiTableBodyRowProps: ({ row }) => {
      const original = row.original as T;
      const selected = selectedRowId != null && String(selectedRowId) === row.id;
      const dimmed = isRowDimmed?.(original) ?? false;

      return {
        hover: false,
        selected,
        ...(interactive
          ? {
              onClick: () => onRowClick?.(original),
              onKeyDown: (event: React.KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onRowClick?.(original);
                }
              },
              tabIndex: 0,
              'aria-selected': selected,
            }
          : {}),
        sx: {
          backgroundColor: base,
          opacity: dimmed ? 0.72 : 1,
          transition: 'background-color .12s ease',
          ...(interactive ? { cursor: 'pointer' } : {}),
          // MUI paints `.Mui-selected` with a translucent primary wash, which
          // on a teal-accented dark theme floods the whole row. Selection here
          // is a rail plus the hover surface, so the wash is overridden rather
          // than composited with.
          ...(selected
            ? {
                '&.Mui-selected, &.Mui-selected:hover': {
                  backgroundColor: dsVar('tableRowHover', backgroundColors.surface),
                  // Brand, read from `brand` rather than from a `text.highlight`
                  // alias of it: marking the current selection is one of the
                  // three jobs the spec reserves brand for.
                  boxShadow: `inset 3px 0 0 ${brand.main}`,
                },
              }
            : {}),
          ...(interactive
            ? {
                '&:hover': {
                  backgroundColor: dsVar('tableRowHover', backgroundColors.surface),
                },
                '&:focus-visible': {
                  outline: `2px solid ${brand.main}`,
                  outlineOffset: '-2px',
                },
              }
            : {}),
        },
      };
    },

    ...rest,

    state: { isLoading: loading, ...(rest.state ?? {}) },
    initialState: { density: 'compact', ...(rest.initialState ?? {}) },
  });

  return <MaterialReactTable table={table} />;
}

export type { MRT_ColumnDef };
